<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: "Modellierung" navigation point — the CwCaseTypeStep plan as a vertical timeline. A row
    click or the „Ändern" action opens the step's dedicated CwCaseTypeStepObjectComponent on the
    right via the Navigation Service (the step-bound Approval / BusinessCheck / ProductBusinessCheck
    config lives there now); the timeline reloads when that drawer closes. „+ Schritt hinzufügen"
    opens the create form with the case type as context. Loading/empty/ready states handled.
  W17: a PRODUCT step also carries its product BINDING as a badge. Several steps of a case type
    legitimately share a name (RC2 has three „formerContractProduct" rows, Kombi FS six), and the
    level tag alone says only that they are product-level — which of the case type's product lines
    each one is pinned to was readable in the drawer form only. An UNPINNED product step gets a grey
    badge instead of a silent gap: it materializes only for a single-line case (see
    CwCaseStepPlanReconciler#resolveProductLine / config check CWCHK-B9), and that is a property of
    the step, not the absence of one.
    The label is `step.productLabel`, resolved by the component from a separate CwCaseTypeProduct
    read — NOT the wire field `caseTypeProductDtoName`, which carries the id as a string
    (CwCaseTypeProductAccess.getText). Binding that one would print „42" as the product name.
-->
<div class="cw-modeling p-3">

    <div class="flex align-items-center justify-content-between mb-4">
        <div class="flex flex-column">
            <span class="text-xl font-semibold">Modellierung</span>
            <span class="text-sm text-gray-600">Prozessschritte des Antragstyps in Reihenfolge</span>
        </div>
        <p-button
            label="Schritt hinzufügen"
            icon="fa-solid fa-plus"
            severity="primary"
            (onClick)="addStep()">
        </p-button>
    </div>

    <!-- Loading -->
    @if (loading) {
        <div class="flex flex-column gap-3">
            @for (i of [1, 2, 3, 4]; track i) {
                <div class="flex align-items-center gap-3">
                    <p-skeleton shape="circle" size="2.5rem"></p-skeleton>
                    <p-skeleton height="4rem" styleClass="flex-1"></p-skeleton>
                </div>
            }
        </div>
    }

    <!-- Empty -->
    @if (!loading && steps.length === 0) {
        <div class="cw-empty flex flex-column align-items-center justify-content-center p-6 text-center">
            <i class="fa-regular fa-diagram-project text-4xl text-gray-400 mb-3"></i>
            <span class="text-lg font-medium mb-1">Noch keine Schritte modelliert</span>
            <span class="text-sm text-gray-600 mb-3">Legen Sie den ersten Prozessschritt an, um den Ablauf dieses Antragstyps zu definieren.</span>
            <p-button label="Ersten Schritt anlegen" icon="fa-solid fa-plus" severity="secondary" (onClick)="addStep()"></p-button>
        </div>
    }

    <!-- Timeline -->
    @if (!loading && steps.length > 0) {
        <div class="cw-timeline">
            @for (step of steps; track step.id) {
                <div class="cw-timeline-row"
                     [class.cw-selected]="selectedStep?.id === step.id"
                     (click)="selectStep(step)">

                    <!-- rail: order bubble + connector -->
                    <div class="cw-rail">
                        <div class="cw-bubble"
                             [class.cw-bubble-case]="step.level === CwStepLevelEnum.CASE"
                             [class.cw-bubble-product]="step.level === CwStepLevelEnum.PRODUCT">
                            {{ step.stepOrder }}
                        </div>
                    </div>

                    <!-- content card -->
                    <div class="cw-card flex-1 p-3">
                        <div class="flex align-items-start justify-content-between gap-2">
                            <div class="flex flex-column gap-2">
                                <div class="flex align-items-center gap-2 flex-wrap">
                                    <span class="text-base font-semibold">{{ step.name }}</span>
                                    @if (step.level === CwStepLevelEnum.CASE) {
                                        <span class="cw-level-tag bg-blue-50 text-blue-700">Vorgang</span>
                                    } @else {
                                        <span class="cw-level-tag bg-purple-50 text-purple-700">Produkt</span>
                                    }
                                </div>
                                <code class="cw-stepkey text-sm text-gray-600">{{ step.stepKey }}</code>
                                <div class="flex align-items-center gap-2 flex-wrap">
                                    @if (step.level === CwStepLevelEnum.PRODUCT) {
                                        @if (step.productLabel) {
                                            <span class="cw-badge bg-purple-50 text-purple-700"
                                                  pTooltip="An diese Produktlinie des Antragstyps gebunden">
                                                <i class="fa-regular fa-box mr-1"></i>{{ step.productLabel }}
                                            </span>
                                        } @else if (!step.caseTypeProductDtoId) {
                                            <span class="cw-badge bg-gray-100 text-gray-700"
                                                  pTooltip="Ohne Bindung greift der Schritt nur, wenn der Vorgang genau eine aktive Produktlinie hat — bei mehreren Linien wird er nicht angelegt.">
                                                <i class="fa-regular fa-box mr-1"></i>Ohne Produktbindung
                                            </span>
                                        }
                                    }
                                    @if (step.optionalStep) {
                                        <span class="cw-badge bg-orange-50 text-orange-700">
                                            <i class="fa-regular fa-circle-half-stroke mr-1"></i>Optional
                                        </span>
                                    }
                                    @if (step.visibilityLogicDtoId) {
                                        <span class="cw-badge bg-teal-50 text-teal-700">
                                            <i class="fa-regular fa-eye mr-1"></i>Sichtbarkeitslogik
                                        </span>
                                    }
                                    @if (step.ufFlowTypeDtoId) {
                                        <span class="cw-badge bg-indigo-50 text-indigo-700">
                                            <i class="fa-regular fa-diagram-project mr-1"></i>UF-Flow
                                        </span>
                                    }
                                </div>
                            </div>
                            <p-button
                                label="Ändern"
                                icon="fa-regular fa-pen"
                                severity="secondary"
                                [text]="true"
                                pTooltip="Schritt-Konfiguration öffnen"
                                (onClick)="editStep(step, $event)">
                            </p-button>
                        </div>
                    </div>
                </div>
            }
        </div>
    }

    <!--
      QUICK QUOTE — the SECOND, independent stack of this case type (design E2). Often a subset of the
      steps above, but it is freely chosen: its own step types, its own order. The Quick Quote view
      reads THIS configuration and never the runtime step plan, which is why the Schritttyp is shown
      on every row — it alone decides which component a Quick-Quote step renders.
      Inactive rows are listed on purpose: hiding what is configured would make a deactivated step
      look deleted.
    -->
    <div class="cw-qq-modeling mt-5">

        <div class="flex align-items-center justify-content-between mb-4">
            <div class="flex flex-column">
                <div class="flex align-items-center gap-2">
                    <i class="fa-solid fa-bolt text-teal-600"></i>
                    <span class="text-xl font-semibold">Quick Quote</span>
                </div>
                <span class="text-sm text-gray-600">
                    Schritte der Schnellerfassung — untereinander dargestellt, ohne Antragsstrecke
                </span>
            </div>
            <p-button
                label="Quick-Quote-Schritt hinzufügen"
                icon="fa-solid fa-plus"
                severity="secondary"
                [outlined]="true"
                (onClick)="addQuickQuoteStep()">
            </p-button>
        </div>

        <!-- Loading -->
        @if (quickQuoteLoading) {
            <div class="flex flex-column gap-3">
                @for (i of [1, 2]; track i) {
                    <div class="flex align-items-center gap-3">
                        <p-skeleton shape="circle" size="2.5rem"></p-skeleton>
                        <p-skeleton height="4rem" styleClass="flex-1"></p-skeleton>
                    </div>
                }
            </div>
        }

        <!-- Empty -->
        @if (!quickQuoteLoading && quickQuoteSteps.length === 0) {
            <div class="cw-empty flex flex-column align-items-center justify-content-center p-6 text-center">
                <i class="fa-regular fa-bolt text-4xl text-gray-400 mb-3"></i>
                <span class="text-lg font-medium mb-1">Kein Quick Quote modelliert</span>
                <span class="text-sm text-gray-600 mb-3">
                    Ohne Quick-Quote-Schritte bietet dieser Antragstyp keine Schnellerfassung an.
                </span>
                <p-button label="Ersten Quick-Quote-Schritt anlegen" icon="fa-solid fa-plus"
                          severity="secondary" (onClick)="addQuickQuoteStep()"></p-button>
            </div>
        }

        <!-- Timeline -->
        @if (!quickQuoteLoading && quickQuoteSteps.length > 0) {
            <div class="cw-timeline">
                @for (step of quickQuoteSteps; track step.id) {
                    <div class="cw-timeline-row"
                         [class.cw-selected]="selectedQuickQuoteStep?.id === step.id"
                         (click)="selectQuickQuoteStep(step)">

                        <div class="cw-rail">
                            <div class="cw-bubble"
                                 [class.cw-bubble-case]="step.level === CwStepLevelEnum.CASE"
                                 [class.cw-bubble-product]="step.level === CwStepLevelEnum.PRODUCT">
                                {{ step.stepOrder }}
                            </div>
                        </div>

                        <div class="cw-card flex-1 p-3">
                            <div class="flex align-items-start justify-content-between gap-2">
                                <div class="flex flex-column gap-2">
                                    <div class="flex align-items-center gap-2 flex-wrap">
                                        <span class="text-base font-semibold">{{ step.name || step.stepKey }}</span>
                                        @if (step.level === CwStepLevelEnum.CASE) {
                                            <span class="cw-level-tag bg-blue-50 text-blue-700">Vorgang</span>
                                        } @else {
                                            <span class="cw-level-tag bg-purple-50 text-purple-700">Produkt</span>
                                        }
                                        @if (!step.active) {
                                            <span class="cw-level-tag bg-gray-100 text-gray-700">Inaktiv</span>
                                        }
                                    </div>
                                    <code class="cw-stepkey text-sm text-gray-600">{{ step.stepKey }}</code>
                                    <div class="flex align-items-center gap-2 flex-wrap">
                                        @if (step.stepTypeLabel) {
                                            <span class="cw-badge bg-teal-50 text-teal-700"
                                                  pTooltip="Der Schritttyp bestimmt, welche Oberfläche im Quick Quote erscheint.">
                                                <i class="fa-regular fa-cube mr-1"></i>{{ step.stepTypeLabel }}
                                            </span>
                                        }
                                        <!--
                                          FD-2b: a configured step the Quick Quote cannot capture is
                                          named HERE, at configuration time. The picker never offers
                                          such a type, but a seed bundle, the generic CRUD form or a
                                          direct API call can write one — and without this badge the
                                          mismatch would only surface at runtime, in the capture
                                          surface's skipped-steps notice, to whoever wanted a premium
                                          rather than to whoever configured the step.
                                        -->
                                        @if (step.notQuickQuoteCapable) {
                                            <span class="cw-badge bg-orange-50 text-orange-700"
                                                  pTooltip="Der Schritttyp wird beim Berechnen nicht erfasst: die Eingaben dieses Schritts fließen nicht in den Beitrag ein. Bitte einen erfassbaren Schritttyp wählen.">
                                                <i class="fa-regular fa-triangle-exclamation mr-1"></i>Wird im Quick Quote übersprungen — Schritttyp nicht erfassbar
                                            </span>
                                        }
                                        @if (step.level === CwStepLevelEnum.PRODUCT && step.productLabel) {
                                            <span class="cw-badge bg-purple-50 text-purple-700"
                                                  pTooltip="An diese Produktlinie des Antragstyps gebunden">
                                                <i class="fa-regular fa-box mr-1"></i>{{ step.productLabel }}
                                            </span>
                                        }
                                    </div>
                                </div>
                                <p-button
                                    label="Ändern"
                                    icon="fa-regular fa-pen"
                                    severity="secondary"
                                    [text]="true"
                                    pTooltip="Quick-Quote-Schritt öffnen"
                                    (onClick)="editQuickQuoteStep(step, $event)">
                                </p-button>
                            </div>
                        </div>
                    </div>
                }
            </div>
        }
    </div>

</div>
